import { useState } from 'react'
import { Check, Circle, Copy, ExternalLink, Loader2 } from 'lucide-react'
import { useI18n } from '../ui/../i18n'
import { Button, Field, Input, cx } from '../ui/kit'

/**
 * The RKSV manual, as a checklist that knows where the landlord actually is.
 *
 * Written as steps rather than prose because that is how this gets done: each one is
 * either finished or it is the next thing to do, and three of them can only happen
 * outside StaySuite. Saying which those are, and in what order, is most of the value
 * — an audit failure here is usually a missed step, not a wrong number.
 *
 * Seit Vorgang 018 (Schnitt 5) erklärt der Guide nur noch den fiskaltrust-Weg:
 * der A-Trust-Tab, dessen Schrittliste und die A-Trust-Zugangsdatenform sind
 * entfernt (F1/AK10 — Renés Wort 18.08.2026; die Function-Pfade `atrust`/`none`
 * bleiben serverseitig stehen, der Legacy-Credentials-Weg antwortet dort 410).
 */

export interface GuideState {
  enabled: boolean
  credentialsSet: boolean
  startbelegAt: string | null
  finanzonlineAt: string | null
  receipts: number
}

export default function RksvGuide({
  state, busy, onSaveCredentials, onRevealKey, onStartbeleg, onFinanzOnline,
}: {
  state: GuideState
  busy: boolean
  onSaveCredentials: (c: Record<string, string>) => Promise<void>
  onRevealKey: () => Promise<string | null>
  onStartbeleg: () => Promise<void>
  onFinanzOnline: (done: boolean) => Promise<void>
}) {
  const { lang, formatDate } = useI18n()
  const de = lang === 'de'

  const [open, setOpen] = useState(false)
  // fiskaltrust identifies the register by CashBox, not by login. The base URL has
  // a default (sandbox) so a test needs only the two values from the portal.
  const [ft, setFt] = useState({ cashbox_id: '', access_token: '', base_url: '' })
  const [aesKey, setAesKey] = useState<string | null>(null)

  const steps = [
    {
      done: state.enabled,
      title: de ? 'Kasse anlegen' : 'Create the register',
      body: de
        ? 'Kassenidentifikationsnummer vergeben (z. B. KASSE-1). Erledigt sich mit dem Formular oben.'
        : 'Give the register an id (e.g. KASSE-1). The form above does this.',
    },
    {
      done: state.credentialsSet,
      title: de ? 'CashBox verbinden' : 'Connect the CashBox',
      body: de
        ? 'Ihre Kasse lebt als Cloud-CashBox bei fiskaltrust — sie signiert, führt das Protokoll und erzeugt das Zertifikat. CashBox-ID und Zugriffstoken stehen im fiskaltrust-Portal unter Konfiguration → CashBox. Hier eintragen:'
        : 'Your register lives as a cloud CashBox at fiskaltrust — it signs, keeps the audit log and issues the certificate. The CashBox id and access token are in the fiskaltrust portal under Configuration → CashBox. Enter them here:',
      extra: (
        <div className="space-y-2 pt-1">
          <Field label="CashBox-ID">
            <Input value={ft.cashbox_id} placeholder="00000000-0000-…"
              onChange={(e) => setFt({ ...ft, cashbox_id: e.target.value })} />
          </Field>
          <Field label={de ? 'Zugriffstoken' : 'Access token'}>
            <Input type="password" value={ft.access_token}
              onChange={(e) => setFt({ ...ft, access_token: e.target.value })} />
          </Field>
          <Field label={de ? 'Endpunkt (leer = Sandbox)' : 'Endpoint (empty = sandbox)'}>
            <Input value={ft.base_url} placeholder="https://possystem-api-sandbox.fiskaltrust.eu/v2"
              onChange={(e) => setFt({ ...ft, base_url: e.target.value })} />
          </Field>
          <Button size="sm" variant="primary"
            disabled={busy || !ft.cashbox_id.trim() || !ft.access_token.trim()}
            onClick={async () => {
              const c: Record<string, string> = {
                cashbox_id: ft.cashbox_id.trim(), access_token: ft.access_token.trim(),
              }
              if (ft.base_url.trim()) c.base_url = ft.base_url.trim()
              await onSaveCredentials(c)
              setFt({ cashbox_id: '', access_token: '', base_url: '' })
            }}>
            {de ? 'Zugangsdaten speichern' : 'Save credentials'}
          </Button>
          <p className="text-xs text-muted">
            {de
              ? 'Die Zugangsdaten werden gespeichert und nie wieder angezeigt — auch uns nicht.'
              : 'Credentials are stored and never shown again — not even to us.'}
          </p>
        </div>
      ),
    },
    {
      done: Boolean(state.finanzonlineAt),
      title: de ? 'Kasse in FinanzOnline registrieren' : 'Register the till in FinanzOnline',
      body: de
        ? 'In FinanzOnline unter „Eingaben → Registrierkassen“ zuerst die Signaturerstellungseinheit, dann die Registrierkasse anmelden. Dafür brauchen Sie die Kassenidentifikationsnummer, die Zertifikatsnummer Ihres Signaturdiensts und den Benutzerschlüssel (AES) unten.'
        : 'In FinanzOnline under "Eingaben → Registrierkassen", register the signature unit first, then the till. You need the register id, the certificate number from your signature service, and the AES user key below.',
      extra: (
        <div className="space-y-2 pt-1">
          <Button size="sm" variant="secondary" disabled={busy}
            onClick={async () => setAesKey(await onRevealKey())}>
            {de ? 'Benutzerschlüssel (AES) anzeigen' : 'Show the AES user key'}
          </Button>
          {aesKey && (
            <div className="rounded-xl bg-surface-2 p-2 flex items-center gap-2">
              <code className="text-xs break-all flex-1">{aesKey}</code>
              <Button size="sm" variant="ghost"
                onClick={() => void navigator.clipboard?.writeText(aesKey)}>
                <Copy size={14} />
              </Button>
            </div>
          )}
          <p className="text-xs text-muted">
            {de
              ? 'Dieser Schlüssel gehört zu Ihrer Kasse. FinanzOnline und die Belegcheck-App brauchen ihn, sonst niemand — bitte nicht weitergeben.'
              : 'This key belongs to your register. FinanzOnline and the Belegcheck app need it, nobody else does — do not pass it on.'}
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(state.finanzonlineAt)}
              onChange={(e) => void onFinanzOnline(e.target.checked)}
            />
            {de ? 'In FinanzOnline registriert' : 'Registered in FinanzOnline'}
          </label>
        </div>
      ),
    },
    {
      done: Boolean(state.startbelegAt),
      title: de ? 'Startbeleg erzeugen und prüfen' : 'Create and check the opening receipt',
      body: de
        ? 'Der Startbeleg ist der erste Beleg der Kasse. Danach mit der BMF-App „BMF Belegcheck“ den QR-Code scannen — erst diese Prüfung bestätigt, dass die Kette stimmt.'
        : 'The opening receipt is the register’s first. Then scan its QR code with the BMF "Belegcheck" app — only that check confirms the chain is sound.',
      extra: (
        <div className="pt-1 space-y-1">
          <Button size="sm" variant="primary"
            disabled={busy || !state.credentialsSet || Boolean(state.startbelegAt)}
            onClick={onStartbeleg}>
            {state.startbelegAt
              ? (de ? `Startbeleg vom ${formatDate(state.startbelegAt)}` : `Opening receipt ${formatDate(state.startbelegAt)}`)
              : (de ? 'Startbeleg erzeugen' : 'Create the opening receipt')}
          </Button>
          {!state.credentialsSet && (
            <p className="text-xs text-warn">
              {de
                ? 'Erst nach Eintragen der Zugangsdaten — ein unsignierter Startbeleg zählt nicht.'
                : 'Only once the credentials are in — an unsigned opening receipt does not count.'}
            </p>
          )}
        </div>
      ),
    },
    {
      done: false,
      title: de ? 'Laufend: Monats- und Jahresbeleg' : 'Ongoing: monthly and annual receipts',
      body: de
        ? 'Am Monatsende ist ein Monatsbeleg fällig, am Jahresende ein Jahresbeleg. StaySuite meldet sich, wenn einer offen ist. Den Jahresbeleg müssen Sie zusätzlich in FinanzOnline prüfen lassen — einmal im Jahr, bis 15. Februar.'
        : 'A monthly receipt is due at each month end and an annual one at year end. StaySuite tells you when one is outstanding. The annual receipt must also be checked in FinanzOnline once a year, by 15 February.',
    },
  ]

  return (
    <div className="pt-1">
      <button
        onClick={() => setOpen(!open)}
        className="text-sm font-medium text-accent underline underline-offset-2"
      >
        {open
          ? (de ? 'Anleitung ausblenden' : 'Hide the guide')
          : (de ? 'Anleitung: was ist wann zu tun?' : 'Guide: what to do, and when')}
      </button>

      {open && (
        <ol className="mt-3 space-y-3">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className={cx('shrink-0 mt-0.5', s.done ? 'text-ok' : 'text-muted')}>
                {busy && !s.done ? <Loader2 size={16} className="animate-spin" />
                  : s.done ? <Check size={16} /> : <Circle size={16} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cx('text-sm font-medium', s.done && 'text-muted line-through')}>
                  {i + 1}. {s.title}
                </p>
                <p className="text-sm text-muted mt-0.5">{s.body}</p>
                {!s.done && s.extra}
              </div>
            </li>
          ))}
        </ol>
      )}

      {open && <FiskaltrustSteps />}
      {open && <Faq />}

      {open && (
        <p className="text-xs text-muted mt-4 flex items-start gap-1.5">
          <ExternalLink size={13} className="shrink-0 mt-0.5" />
          {de
            ? 'FinanzOnline und die Belegcheck-App sind Sache des Finanzamts — StaySuite kann diese Schritte nicht für Sie erledigen, nur daran erinnern.'
            : 'FinanzOnline and the Belegcheck app belong to the tax office — StaySuite can remind you of those steps but cannot perform them.'}
        </p>
      )}
    </div>
  )
}

/* --------------------------------------------------------- ordering steps */

/**
 * How the register actually reaches the landlord. Written as the order things
 * happen in, because that is the question behind every support mail here:
 * bestellen → AUVISO provisioniert → Startbeleg → aktiv, plus the FinanzOnline
 * steps only the landlord can do.
 */
function FiskaltrustSteps() {
  const { lang } = useI18n()
  const de = lang === 'de'

  const steps = de ? [
    'Das Zusatzmodul Fiskalisierung in den Einstellungen bestellen — mit bestätigter Kostenübersicht.',
    'Wichtig davor: UID- bzw. Steuernummer korrekt angeben und bestätigen. Eine falsche Nummer fällt erst bei der FinanzOnline-Meldung auf und verlangt einen Zertifikatstausch über den Support — dieser Aufwand wird verrechnet.',
    'AUVISO richtet Ihre Cloud-CashBox bei fiskaltrust ein — eine je Betrieb, sie erzeugt auch Ihr Signaturzertifikat. Der Kassenvertrag läuft dabei auf Ihre Firma; ein eigenes fiskaltrust-Konto brauchen Sie im Normalbetrieb nicht.',
    'Danach den Startbeleg erzeugen (Schritt 4 oben) und die FinanzOnline-Schritte erledigen. fiskaltrust kann die FinanzOnline-Meldung übernehmen, wenn dort FinanzOnline-Webservice-Zugangsdaten hinterlegt sind — dann entfällt Schritt 3 weitgehend.',
  ] : [
    'Order the fiscalisation add-on in Settings — with the cost summary confirmed.',
    'Before that: enter and confirm your VAT id or tax number correctly. A wrong number only surfaces at the FinanzOnline registration and requires a certificate swap via support — that effort is billed.',
    'AUVISO sets up your cloud CashBox at fiskaltrust — one per business, and it also issues your signature certificate. The register contract is in your company’s name; you do not need your own fiskaltrust account in normal operation.',
    'Then create the opening receipt (step 4 above) and complete the FinanzOnline steps. fiskaltrust can file the FinanzOnline registration for you if web-service credentials are stored there, which largely removes step 3.',
  ]

  return (
    <div className="mt-5">
      <h3 className="text-sm font-medium mb-2">
        {de ? 'So kommt die Kasse zu Ihnen — Schritt für Schritt' : 'How the register reaches you — step by step'}
      </h3>
      <ol className="text-sm space-y-1.5 list-decimal pl-5">
        {steps.map((t, i) => <li key={i} className="text-muted">{t}</li>)}
      </ol>
    </div>
  )
}

/* --------------------------------------------------------------------- FAQ */

function Faq() {
  const { lang } = useI18n()
  const de = lang === 'de'

  const items: [string, string][] = de ? [
    ['Brauche ich überhaupt eine Registrierkasse?',
     'Nur wenn beide Grenzen überschritten sind: mehr als 15.000 € Jahresumsatz UND mehr als 7.500 € davon als Barumsatz. StaySuite rechnet das laufend mit und meldet sich rechtzeitig.'],
    ['Zählt eine Kartenzahlung als Barumsatz?',
     'Ja. Bankomat- und Kreditkarte zählen als Barumsatz, auch wenn online gezahlt wird. Nur die Überweisung zählt nicht. Deshalb kann man ohne einen einzigen Geldschein über der Grenze liegen.'],
    ['Ab wann gilt die Pflicht?',
     'Ab dem viertfolgenden Monat nach Ende des Quartals, in dem beide Grenzen überschritten wurden. Wer im August überschreitet, ist ab 1. Jänner dran — also rund drei Monate Vorlauf.'],
    ['Was kostet mich das?',
     'Das Zusatzmodul Fiskalisierung wird monatlich zusätzlich zum Paket berechnet; den aktuellen Betrag zeigt die Kostenübersicht, bevor Sie bestellen. Mindestbezugsdauer zwölf Monate, danach monatlich zum Monatsende kündbar.'],
    ['Muss der Gast einen Beleg bekommen?',
     'Ja, Belegerteilungspflicht. Der Beleg darf elektronisch sein, wenn der Gast damit einverstanden ist — die Rechnung per E-Mail genügt also.'],
    ['Was ist der Startbeleg?',
     'Der erste Beleg der Kasse. Er wird signiert und danach mit der App „BMF Belegcheck" geprüft. Erst diese Prüfung bestätigt, dass die Belegkette korrekt aufgesetzt ist.'],
    ['Wozu Monats- und Jahresbeleg?',
     'Sie enthalten keinen Umsatz, sondern signieren den Umsatzzähler zum Stichtag. Damit ist belegt, dass zwischendurch nichts verschwunden ist. Der Jahresbeleg muss zusätzlich bis 15. Februar in FinanzOnline geprüft werden.'],
    ['Was, wenn der Signaturdienst ausfällt?',
     'Der Beleg wird trotzdem erstellt und als „Sicherheitseinrichtung ausgefallen" gekennzeichnet — genau so sieht es die Verordnung vor. Dauert der Ausfall länger als 48 Stunden, ist er in FinanzOnline zu melden.'],
    ['Was passiert bei einer Prüfung?',
     'Das Finanzamt verlangt das Datenerfassungsprotokoll. Das exportiert StaySuite auf Knopfdruck; zusätzlich legt StaySuite wöchentlich einen Abzug bei sich ab. Belege und Protokoll sind sieben Jahre aufzubewahren — diese Pflicht bleibt bei Ihnen.'],
    ['Kann ich Belege nachträglich ändern?',
     'Nein, und das ist der Sinn der Sache. Eine ausgestellte Rechnung wird mit einer Gutschrift storniert, nicht bearbeitet.'],
  ] : [
    ['Do I even need a cash register?',
     'Only if both limits are exceeded: over €15,000 turnover a year AND over €7,500 of it in cash-like payments. StaySuite tracks this continuously and warns you in time.'],
    ['Does a card payment count as cash?',
     'Yes. Debit and credit cards count, including online. Only a bank transfer does not. That is how a landlord can be over the line without ever handling a banknote.'],
    ['When does the obligation start?',
     'From the fourth month after the end of the quarter in which both limits were passed. Crossing in August means being obliged from 1 January — roughly three months of warning.'],
    ['What does it cost?',
     'The fiscalisation add-on is billed monthly on top of your plan; the cost summary shows the current figure before you order. Minimum term of twelve months, thereafter cancellable monthly to the end of a month.'],
    ['Must the guest receive a receipt?',
     'Yes. It may be electronic if the guest agrees, so the invoice by email is enough.'],
    ['What is the Startbeleg?',
     'The register’s first receipt. It is signed and then checked with the BMF Belegcheck app — only that check confirms the chain was set up correctly.'],
    ['Why monthly and annual receipts?',
     'They carry no turnover; they sign the counter at a cut-off date, proving nothing went missing in between. The annual one must also be checked in FinanzOnline by 15 February.'],
    ['What if the signature service fails?',
     'The receipt is still created and marked "Sicherheitseinrichtung ausgefallen", which is what the regulation prescribes. An outage longer than 48 hours must be reported in FinanzOnline.'],
    ['What happens in an audit?',
     'The tax office asks for the audit log (DEP). StaySuite exports it in one click, and additionally files a weekly copy on its side. Receipts and log must be kept for seven years — that duty stays with you.'],
    ['Can I change a receipt afterwards?',
     'No, and that is the point. An issued invoice is reversed with a credit note, never edited.'],
  ]

  return (
    <div className="mt-5">
      <h3 className="text-sm font-medium mb-2">{de ? 'Häufige Fragen' : 'Common questions'}</h3>
      <div className="divide-y divide-border">
        {items.map(([q, a], i) => (
          <details key={i} className="py-2">
            <summary className="text-sm font-medium cursor-pointer list-none marker:hidden">
              {q}
            </summary>
            <p className="text-sm text-muted mt-1.5">{a}</p>
          </details>
        ))}
      </div>
    </div>
  )
}
